import React, { useEffect, useState, useCallback } from 'react';

/**
 * Inbox view inside the Auto Apply page. Mirrors AIApply's split-pane
 * layout: list on the left, selected message body on the right. Pulls
 * messages straight from /api/inbox/messages (which proxies Nylas).
 *
 * Rendered only when the parent has confirmed `nylas.connected === true`,
 * so we don't try to fetch when there's no grant.
 */
export default function InboxView({ apiBaseUrl, isActive = true }) {
  const [messages, setMessages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedBody, setSelectedBody] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingBody, setLoadingBody] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread' | 'job-related'
  const [iframeHeight, setIframeHeight] = useState(400);

  const token = () => localStorage.getItem('token');

  // Listen for height updates posted by the email iframe so the frame
  // grows to fit the full email — no inner scrollbar, page scroll instead.
  useEffect(() => {
    const handler = (e) => {
      if (e.data && e.data.type === 'terview-email-height' && typeof e.data.height === 'number') {
        setIframeHeight(Math.max(200, Math.min(e.data.height + 24, 20000)));
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Reset to a sensible default when switching emails so we don't briefly
  // show the previous email's height before the new one reports back.
  useEffect(() => { setIframeHeight(400); }, [selectedId]);

  // silent=true skips the loading spinner so the periodic auto-refresh
  // doesn't blink the UI every minute. Manual 🔄 always shows the
  // spinner AND triggers a server-side sync (POST /api/inbox/sync) so
  // the user gets the freshest possible view from Nylas, not just the
  // last-cached webhook state.
  const fetchList = useCallback(async (silent = false) => {
    if (!silent) {
      setLoadingList(true);
      // Manual refresh — ask the server to pull fresh from Nylas before
      // re-reading the cache. Best-effort; if sync fails (rate limit,
      // network), the subsequent list read still returns cached data.
      try {
        await fetch(`${apiBaseUrl}/api/inbox/sync`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token()}` },
        });
      } catch (_) { /* fall through to list read */ }
    }
    setError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/api/inbox/messages?limit=50`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setMessages(data.messages || []);
    } catch (err) {
      setError(err.message);
    } finally {
      if (!silent) setLoadingList(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Auto-refresh — polls Nylas every 60s so new emails appear without
  // the user clicking 🔄. Pauses when:
  //   • the inbox sub-tab is hidden (display:none — saves bandwidth)
  //   • the browser tab is backgrounded (document.hidden — saves
  //     bandwidth + avoids waking laptops on battery)
  // True real-time would require Nylas webhooks → server push (WS/SSE);
  // 60s polling is the right tradeoff for now.
  useEffect(() => {
    if (!isActive) return;
    const tick = () => {
      if (document.hidden) return;
      fetchList(true);
    };
    const id = setInterval(tick, 60_000);
    // Also re-fetch immediately when the browser tab regains focus,
    // so coming back from another window shows fresh mail right away.
    const onVisible = () => { if (!document.hidden) fetchList(true); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isActive, fetchList]);

  const openMessage = useCallback(async (msgId) => {
    setSelectedId(msgId);
    setSelectedBody(null);
    setLoadingBody(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/inbox/messages/${encodeURIComponent(msgId)}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSelectedBody(data);
      // Optimistically mark read locally so the badge drops without re-fetch.
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, unread: false } : m));
      // Best-effort persist read state on Nylas's side.
      fetch(`${apiBaseUrl}/api/inbox/messages/${encodeURIComponent(msgId)}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ unread: false }),
      }).catch(() => {});
    } catch (err) {
      setSelectedBody({ subject: 'Error', bodyHtml: `<p style="color:#f87171">Failed to load: ${err.message}</p>` });
    } finally {
      setLoadingBody(false);
    }
  }, [apiBaseUrl]);

  // Job-related = sender domain matches a known ATS or contains the word
  // "careers", "talent", "recruit", etc. Cheap heuristic — the LLM
  // classifier is Day 2.
  const isJobRelated = (m) => {
    const email = (m.from?.email || '').toLowerCase();
    const subject = (m.subject || '').toLowerCase();
    const blob = email + ' ' + subject;
    return /greenhouse-mail\.io|lever\.co|workable\.com|ashbyhq\.com|smartrecruiters|myworkdayjobs|icims|careers|talent|recruit|application|job|interview/.test(blob);
  };

  const visible = messages.filter(m =>
    filter === 'all'         ? true :
    filter === 'unread'      ? m.unread :
    filter === 'job-related' ? isJobRelated(m) :
    true
  );

  const formatDate = (epochSeconds) => {
    if (!epochSeconds) return '';
    const d = new Date(epochSeconds * 1000);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Deterministic color per sender — same email always gets the same hue.
  const avatarColor = (seed = '') => {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    const hue = h % 360;
    return `hsl(${hue}, 55%, 45%)`;
  };
  const avatarInitial = (m) => {
    const s = m.from?.name || m.from?.email || '?';
    return s.trim().charAt(0).toUpperCase() || '?';
  };

  return (
    <div style={{
      marginTop: 16, padding: 0, borderRadius: 16,
      border: '1px solid rgba(224, 163, 255, 0.18)',
      background: 'linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.02))',
      backdropFilter: 'blur(18px)',
      WebkitBackdropFilter: 'blur(18px)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.30), 0 0 0 1px rgba(255,255,255,0.04) inset',
      overflow: 'hidden',
      // Hide via display:none when the parent sub-tab is "jobs". The
      // component stays mounted, so the cached messages + selectedBody
      // survive tab switches and we don't re-fetch on every toggle.
      display: isActive ? 'grid' : 'none',
      gridTemplateColumns: '380px minmax(0, 1fr)',
      height: 'calc(100vh - 240px)',
      maxWidth: '100%',
    }}>
      {/* ── LEFT: list ── */}
      <div style={{
        borderRight: '1px solid rgba(224, 163, 255, 0.14)',
        display: 'flex', flexDirection: 'column',
        minHeight: 0,
      }}>
        {/* Filter strip */}
        <div style={{
          padding: '14px 16px', borderBottom: '1px solid rgba(224, 163, 255, 0.14)',
          display: 'flex', alignItems: 'center', gap: 10,
          flexShrink: 0,
        }}>
          <label style={{
            color: 'rgba(224,163,255,0.7)', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>Filter</label>
          <select value={filter} onChange={e => setFilter(e.target.value)} style={{
            padding: '6px 10px', borderRadius: 8,
            background: 'rgba(255,255,255,0.035)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.1)', fontSize: 12.5,
            cursor: 'pointer',
          }}>
            <option value="all">All</option>
            <option value="unread">Unread</option>
            <option value="job-related">Job-related</option>
          </select>
          <button
            onClick={fetchList}
            disabled={loadingList}
            style={{
              marginLeft: 'auto', padding: '6px 10px', borderRadius: 8,
              background: 'rgba(167,139,250,0.10)', color: '#c4b5fd',
              border: '1px solid rgba(167,139,250,0.35)', cursor: 'pointer', fontSize: 12,
              transition: 'background 0.15s',
            }}>
            {loadingList ? '⏳' : '🔄'}
          </button>
        </div>

        {/* List — scrollbar hidden for clean look */}
        <div className="ajp-inbox-list" style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {loadingList && messages.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
              Loading…
            </div>
          )}
          {error && (
            <div style={{ padding: 20, color: '#f87171', fontSize: 13 }}>
              Error: {error}
            </div>
          )}
          {!loadingList && visible.length === 0 && !error && (
            <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
              No messages.
            </div>
          )}
          {visible.map(m => {
            const isActive = m.id === selectedId;
            const seed = m.from?.email || m.from?.name || '';
            return (
              <button
                key={m.id}
                className="ajp-inbox-item"
                onClick={() => openMessage(m.id)}
                style={{
                  display: 'flex', width: '100%', textAlign: 'left',
                  alignItems: 'flex-start', gap: 10,
                  padding: '13px 16px',
                  borderTop: 'none',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                  borderLeft: isActive ? '3px solid #a78bfa' : '3px solid transparent',
                  borderRight: 'none',
                  background: isActive ? 'linear-gradient(90deg, rgba(167,139,250,0.18), rgba(167,139,250,0.04))' : 'transparent',
                  color: '#fff',
                  cursor: 'pointer',
                  transition: 'background 0.15s, border-color 0.15s',
                }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: avatarColor(seed),
                  color: '#fff', fontSize: 13, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, marginTop: 2,
                  position: 'relative',
                }}>
                  {avatarInitial(m)}
                  {m.unread && (
                    <span style={{
                      position: 'absolute', top: -2, right: -2,
                      width: 10, height: 10, borderRadius: '50%',
                      background: '#7c3aed', border: '2px solid rgba(20,10,40,1)',
                    }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    marginBottom: 3, gap: 8,
                  }}>
                    <div style={{
                      fontSize: 13, fontWeight: m.unread ? 700 : 500,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      color: m.unread ? '#fff' : 'rgba(255,255,255,0.75)',
                    }}>
                      {m.from?.name || m.from?.email || '(unknown)'}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>
                      {formatDate(m.date)}
                    </div>
                  </div>
                  <div style={{
                    fontSize: 12.5,
                    fontWeight: m.unread ? 600 : 400,
                    color: m.unread ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.65)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    marginBottom: 2,
                  }}>
                    {m.subject}
                  </div>
                  <div style={{
                    fontSize: 11.5, color: 'rgba(255,255,255,0.4)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {m.snippet}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── RIGHT: body — own internal scroll so the iframe email can
          grow without pushing the page past the fold ── */}
      <div className="ajp-inbox-body" style={{
        padding: '22px 26px', overflowY: 'auto', minWidth: 0, minHeight: 0,
      }}>
        {!selectedId && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            height: '100%', color: 'rgba(255,255,255,0.4)', textAlign: 'center',
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
            <div style={{ fontSize: 14 }}>No email selected</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Click a message on the left to view it.</div>
          </div>
        )}
        {selectedId && loadingBody && (
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>Loading…</div>
        )}
        {selectedId && selectedBody && !loadingBody && (
          <>
            <h2 style={{
              margin: '0 0 14px', fontSize: 15, color: '#f1f5f9', fontWeight: 700,
              lineHeight: 1.35, letterSpacing: '-0.005em', textAlign: 'left',
            }}>
              {selectedBody.subject}
            </h2>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              marginBottom: 14, paddingBottom: 12,
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              textAlign: 'left',
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: avatarColor(selectedBody.from?.email || selectedBody.from?.name || ''),
                color: '#fff', fontSize: 13, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                {(selectedBody.from?.name || selectedBody.from?.email || '?').trim().charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <div style={{ fontSize: 12.5, color: '#fff', fontWeight: 600, lineHeight: 1.3 }}>
                  {selectedBody.from?.name || selectedBody.from?.email}
                </div>
                {selectedBody.from?.email && selectedBody.from?.name && (
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 1 }}>
                    {selectedBody.from.email}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', flexShrink: 0 }}>
                {formatDate(selectedBody.date)}
              </div>
            </div>
            {/* Render email HTML in a sandboxed iframe so its fixed-pixel
                tables/images can't push the outer column wider. The iframe
                also fully isolates email CSS/scripts from the app. The
                small inline script measures content height and posts it to
                the parent so the iframe grows to fit — no inner scrollbar.
                sandbox="allow-scripts" runs the resize script but keeps
                the iframe in an opaque origin (no access to app cookies). */}
            <div style={{
              borderRadius: 10,
              overflow: 'hidden',
              background: '#fafafa',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
            }}>
              <iframe
                key={selectedId}
                title={selectedBody.subject || 'Email'}
                sandbox="allow-scripts allow-popups"
                srcDoc={`<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>
                  html,body{margin:0;padding:18px 22px;background:#fafafa;color:rgba(0,0,0,0.82);font-family:system-ui,-apple-system,sans-serif;font-size:14px;line-height:1.55;word-break:break-word;}
                  img{max-width:100%!important;height:auto!important;}
                  table{max-width:100%!important;}
                  a{color:#6d28d9;}
                </style></head><body>${selectedBody.bodyHtml || ''}<script>
                  (function(){
                    function post(){
                      var h=Math.max(document.body.scrollHeight,document.documentElement.scrollHeight,document.body.offsetHeight,document.documentElement.offsetHeight);
                      parent.postMessage({type:'terview-email-height',height:h},'*');
                    }
                    post();
                    window.addEventListener('load',post);
                    if(window.ResizeObserver){new ResizeObserver(post).observe(document.body);}
                    document.querySelectorAll('img').forEach(function(i){if(!i.complete){i.addEventListener('load',post);i.addEventListener('error',post);}});
                  })();
                </\u0073cript></body></html>`}
                style={{
                  width: '100%',
                  height: iframeHeight,
                  border: 'none',
                  background: '#fafafa',
                  display: 'block',
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
